import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpParams} from "@angular/common/http";
import {BehaviorSubject, firstValueFrom, Subject} from "rxjs";
import {SessionService} from "./session.service";
import {Appointment, DateUtils, Patient, PatientInfo, User} from "@allaihealth/util";
import {
	AppointmentsResponse,
	AppointmentStatus,
	AppointmentTask,
	DocTask,
	IdTask,
	InfoTask,
	InsuranceTask,
	PaymentTask
} from "intake-shared";
import {PageLoaderService, SnackbarService} from "@allaihealth/ng-platform";
import {environment} from "../../environments/environment";
import {ActivatedRouteSnapshot, Router} from "@angular/router";

/** Service that loads and caches all appointments in the appointment list page */
@Injectable({
	providedIn: 'root'
})
export class AppointmentsService {
	/** Loaded appointments by id */
	private _appointmentMap:Map<string, Appointment>;

	/** All appointments sorted by start time */
	private _sortedAppointments:Appointment[];

	/** loaded _patients by their id */
	private _patientMap:Map<string, Patient>;

	/** Loaded providers by their id */
	private _providerMap:Map<string, User>;
	private _sortedProviders:User[];

	private _sortedStatuses:string[] = [
		'Scheduled','Arrived', 'Checked In', 'In Visit', 'Checked Out'
	]

	/** Loaded tasks by appointment id */
	private _taskMap:Map<string, AppointmentTask>;
	private _patientInfoMap:Map<string, PatientInfo>;

	/** Subject for a new appointments list, usually from a filter change */
	private newAppointmentsSubject = new Subject<void>();

	/** Subject for when a new appointment is transitioned to */
	private newAppointmentSubject = new BehaviorSubject<Appointment>(undefined);

	private _detailUpdated = new BehaviorSubject<any>(null);
	private _currentDate:Date;

	/** Detail data for the current appointment */
	private _appointment:any;
	private _patient:any;
	private _patientInfo:PatientInfo;
	private _provider:User;
	private _tasks:AppointmentTask;
	private _taskValues:{ [key:string]:boolean };
	private _taskValueMap:Map<string, { [key:string]:boolean }>;

	private _sortedTasks = [
		'HEADER_Type','Info', 'Doc', 'ID', 'Insurance', 'Payment','HEADER_Status', 'Not Started', 'In Progress', 'Complete', 'HEADER_  '
	]

	constructor(private http:HttpClient,
	            private router:Router,
	            private sessionService:SessionService,
	            private pageLoaderService:PageLoaderService,
	            private snackbar:SnackbarService) {
		//clear things out if the user logs out
		this.sessionService.onLogout.subscribe(() => {
			this.clear();
		});
	}

	/** Resolver to get all appointments for the list if not already loaded */
	static resolveAppointments() {
		return inject(AppointmentsService).resolveAppointments();
	}

	/** Resolver to get all appointments for the list if not already loaded */
	async resolveAppointments(hideLoader?:boolean) {
		try {
			if(!hideLoader)
				this.pageLoaderService.show("loading appointments");

			let params:HttpParams = new HttpParams();
			const zone = this.sessionService.organization.time_zone;
			const date = DateUtils.setZonedStartEndDate(this._currentDate ?? new Date(), true, zone);
			params = params.set("date", date.toMillis());
			const response = await firstValueFrom(this.http.get<AppointmentsResponse>(
				`${environment.apiUrl}/appointments`,
				{params}
			));

			this._appointmentMap = new Map();

			//go through each appointment and add to the map
			for(const appointment of response.appointments) {
				//set the date since dates can't be serialized in JSON
				appointment.appointment_date = new Date(appointment.appointment_date);

				this._appointmentMap.set(appointment.id, appointment);
			}

			//sort the appointments
			this._sortedAppointments = response.appointments?.sort((a, b) =>
				a.appointment_date.getTime() - b.appointment_date.getTime());

			//add the _patients
			this._patientMap = new Map(response.patients?.map(value => {
				value.birthdate = DateUtils.convertBirthDate(value.birthdate);
				return [value.id, value];
			}));

			this._providerMap = new Map(response.providers?.map(value => [value.id, value]));
			const providerSet = new Set(response.providers);
			this._sortedProviders = [...providerSet];
			this._sortedProviders.sort((a:User, b:User) => {
				return a.first_name.localeCompare(b.first_name) || a.last_name.localeCompare(b.last_name);
			});

			this._taskMap = new Map(response.tasks?.map(value => [value.appointmentId, value]));
			this._taskValueMap = new Map();
			this._patientInfoMap = new Map(response.patientInfos?.map(value => [value.patientId, value]))

			//dispatch that there was a change
			this.newAppointmentsSubject.next();
		} catch(e) {
			console.error("Failed to load appointments from server", e);
			this.snackbar.error("Failed to download appointments");
			return false;
		} finally {
			this.pageLoaderService.close();
		}

		return true;
	}

	/** Resolver to a single appointment from the route id */
	static canActivateAppointment(route:ActivatedRouteSnapshot) {
		return inject(AppointmentsService).canActivateAppointment(route);
	};

	/** Resolver to a single appointment from the route id */
	private canActivateAppointment(route:ActivatedRouteSnapshot) {
		if(!this._appointmentMap) {
			return this.router.createUrlTree(["/appointments"]);
		}
		const id = route.paramMap.get("id");

		this._appointment = this._appointmentMap.get(id);

		if(this._appointment) {
			this.loadTaskValues(this._appointment.id);
			this._patient = this._patientMap.get(this._appointment.id_patient);
			this._provider = this._providerMap.get(this._appointment.id_provider);
			this._patientInfo = this._patientInfoMap.get(this._patient.id);
			if(!this._patientInfo) {
				this._patientInfo = {
					patientId: this._patient.id,
					organizationId: this._patient.id_organization,
					data: {}
				}
			}
			this.newAppointmentSubject.next(this._appointment);
			return true;
		}

		return this.router.createUrlTree(["/appointments"]);
	}

	/** Clear out loaded appointments and force a reload */
	clear() {
		this._sortedAppointments = undefined
		this._appointmentMap = undefined;
		this._patientMap = undefined;
		this._providerMap = undefined;
		this._patientInfoMap = undefined;
		this._appointment = undefined;
		this._patient = undefined;
		this._tasks = undefined;
		this._taskValueMap = undefined;
		this._taskValues = undefined;
		this._patientInfo = undefined;
	}

	/** Get a patient by id */
	getPatient(id:string) {
		return this._patientMap.get(id);
	}

	/** Dispatched for new appointments lists, usually from a filter change */
	get onNewAppointments() {
		return this.newAppointmentsSubject.asObservable();
	}

	/** Dispatched for newly selected appointment */
	get onNewAppointment() {
		return this.newAppointmentSubject.asObservable();
	}

	get onDetailUpdate() {
		return this._detailUpdated.asObservable();
	}

	loadTaskValues(appointmentId:string) {
		this._tasks = this._taskMap.get(appointmentId);
		this._taskValues = this._taskValueMap.get(appointmentId);
		if(!this._taskValues) {
			const tasks = this._taskMap.get(appointmentId);
			this._taskValues = {};
			for(const [categoryKey, category] of Object.entries(tasks.tasks)) {
				for(const [valueKey, value] of Object.entries(category)) {
					this._taskValues[`${categoryKey}.${valueKey}`] = value as boolean;
				}
			}
			this._taskValueMap.set(appointmentId, this._taskValues);
		}
	}

	get sortedAppointments():Appointment[] {
		return this._sortedAppointments;
	}

	get tasks() {
		return this._tasks;
	}

	async updateStatus(appointmentId:string, status:AppointmentStatus) {
		return await firstValueFrom(this.http.put(`${environment.apiUrl}/appointment/${appointmentId}/status/overall/${status}`, {}));
	}

	async updateTask(taskKey:string, valueKey:string, value:boolean) {
		await firstValueFrom(this.http.put(`${environment.apiUrl}/appointment/${this._appointment.id}/${taskKey}/${valueKey}/${value}`, {}));
		this.setValue(taskKey, valueKey, value);
		this._detailUpdated.next({appointmentId: this._appointment.id, taskKey, valueKey, value});
	}

	async infoUpdated() {
		this._detailUpdated.next({});
	}

	setValue(taskKey:string, valueKey:string, value:boolean) {
		if(taskKey == "info") {
			const key:keyof InfoTask = valueKey as keyof InfoTask;
			this._tasks.tasks.info[key] = value;
		} else if(taskKey == "doc") {
			const key:keyof DocTask = valueKey as keyof DocTask;
			this._tasks.tasks.doc[key] = value;

		}
		if(taskKey == "id") {
			const key:keyof IdTask = valueKey as keyof IdTask;
			this._tasks.tasks.id[key] = value;
		}
		if(taskKey == "insurance") {
			const key:keyof InsuranceTask = valueKey as keyof InsuranceTask;
			this._tasks.tasks.insurance[key] = value;

		}
		if(taskKey == "payment") {
			const key:keyof PaymentTask = valueKey as keyof PaymentTask;
			this._tasks.tasks.payment[key] = value;
		}
	}

	get taskValues() {
		return this._taskValues;
	}

	set currentDate(date:Date) {
		this._currentDate = date;
		this.resolveAppointments(false).then();
	}

	get currentDate() {
		if(!this._currentDate) {
			this.currentDate = new Date();
		}
		return this._currentDate;
	}

	async savePatient() {
		return await firstValueFrom(this.http.put(`${environment.apiUrl}/patient/${this._patient.id}`, {
			patient: this._patient,
			patientInfo: this._patientInfo
		}));
	}

	get patient() {
		return this._patient;
	}

	get provider() {
		return this._provider;
	}

	get patientInfo() {
		return this._patientInfo;
	}

	getProvider(providerId:string) {
		return this._providerMap.get(providerId);
	}

	getTasks(appointmentId:string) {
		return this._taskMap.get(appointmentId);
	}

	getPatientInfo(id:string) {
		return this._patientInfoMap.get(id);
	}

	get sortedProviders() {
		return this._sortedProviders;
	}

	get sortedStatuses() {
		return this._sortedStatuses;
	}

	get sortedTasks() {
		return this._sortedTasks;
	}
}
